import React, { useEffect, useState } from 'react';
import {
    Typography,
    CircularProgress,
    Box,
    Paper,
    Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {useHistory, useLocation} from 'react-router-dom';
import {getStripeSubscriptionData} from "../../api/signupApi";
import {STRIPE_PAYMENT_USER_TYPE} from "../../constants/CoreConstants";
import {useDispatch} from "react-redux";
import {signupWithStripeUniqueCode} from "../../actions/signupAction";
import {fetchAccountData} from "../../api/loginApi";
import {deleteCookie, setCookie} from "../../helpers/Cookie";
import Utils from "../../helpers/Utils";

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100vh',
        backgroundColor: '#f2f7f9',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    paper: {
        padding: theme.spacing(4),
        textAlign: 'center',
        maxWidth: 450,
        width: '100%',
    },
    title: {
        marginBottom: theme.spacing(2),
    },
    successText: {
        color: theme.palette.success.main,
        marginTop: theme.spacing(2),
    },
    errorText: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(2),
    },
    progress: {
        '&.MuiCircularProgress-root': {
            position: 'relative'
        }
    }
}));

const StripeSuccessPage = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('Oops! Something went wrong.');
    const [redirect, setRedirect] = useState('');
    const uniqueCodeErrorMessage = 'Please reload the page.';

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const uniqueCode = queryParams.get('unique_code');
        const planType = queryParams.get('type');

        if (!uniqueCode || !planType) {
            setStatus('error');
            return;
        }
        const retryIntervals = [5000, 7000, 10000, 15000];
        let retryIndex = 0;
        let timeoutId = null;
        const verifyPayment = async () => {
            try {
                const response = await getStripeSubscriptionData({ unique_code: uniqueCode });
                if (response?.data && response.data?.success && response.data?.data) {
                    handleSuccess(response.data.data);
                } else {
                    const message = response?.data?.message || 'Oops! Something went wrong.';
                    if (message === "Unique code doesnt exist") {
                        if (retryIndex < retryIntervals.length) {
                            timeoutId = setTimeout(verifyPayment, retryIntervals[retryIndex]);
                            retryIndex++;
                        } else {
                            setStatus('error');
                            setMessage(uniqueCodeErrorMessage);
                        }
                    } else {
                        setStatus('error');
                        setMessage(message);
                        setRedirect(`/${planType}/get-started`);
                    }
                }
            } catch (error) {
                setStatus('error');
                setMessage('Oops! Something went wrong.');
            }
        };
    
        const handleSuccess = (stripeSubscriptionData) => {
            const metadata = stripeSubscriptionData.metadata;

            if (metadata.email && metadata.phone && stripeSubscriptionData.user_type === STRIPE_PAYMENT_USER_TYPE.NEW_USER) {
                const ROLE_OWNER = 1;
                const signupData = {
                    name: metadata.name,
                    full_name: metadata.full_name,
                    phone: metadata.phone,
                    email: metadata.email,
                    hear_about_us_item_id: metadata.about_us_item ? JSON.parse(metadata.about_us_item) : null,
                    company_category_id: metadata.company_category_id ? JSON.parse(metadata.company_category_id) : null,
                    unique_code: metadata.unique_code,
                    agency_id: metadata.agency_id ? JSON.parse(metadata.agency_id) : null,
                    autoSubmitting: true,
                    role_id: ROLE_OWNER
                };
                dispatch(signupWithStripeUniqueCode(signupData, () => {
                    setTimeout(() => history.push({
                        pathname: `/${planType}/get-started`,
                        search: `?unique_code=${uniqueCode}`
                    }), 3000);
                }));
                setStatus('success');
            } else if ((!metadata.email || !metadata.phone) && stripeSubscriptionData.user_type === STRIPE_PAYMENT_USER_TYPE.NEW_USER) {
                setStatus('success');
                setTimeout(() => history.push({
                    pathname: `/${planType}/get-started`,
                    search: `?unique_code=${uniqueCode}`
                }), 3000);
            } else if (stripeSubscriptionData.user_type === STRIPE_PAYMENT_USER_TYPE.OLD_USER) {
                setStatus('success');
                fetchAccountData().then(response => {
                    if (response?.data.status && response?.data.data) {
                        const userAccountData = { ...response.data.data };
                        if (userAccountData.payment_failed) delete userAccountData.payment_failed;
                        setCookie(process.env.REACT_APP_ACCOUNT_DATA, JSON.stringify(userAccountData), process.env.REACT_APP_ACCESS_TOKEN_VALIDITY);
                    } else {
                        deleteCookie(process.env.REACT_APP_ACCESS_TOKEN);
                        deleteCookie(process.env.REACT_APP_ACCOUNT_DATA);
                        Utils.showNotification('Oops! Something went wrong. Please reload the page', 'error');
                    }
                }).catch(() => {
                    deleteCookie(process.env.REACT_APP_ACCESS_TOKEN);
                    deleteCookie(process.env.REACT_APP_ACCOUNT_DATA);
                }).finally(() => {
                    setTimeout(() => history.push({
                        pathname: '/',
                        state: { popup: 'PAYMENT_SUCCESS' }
                    }), 3000);
                });
            }
        };
        verifyPayment();
    
        return () => clearTimeout(timeoutId);
    
    }, [location.search, history]);

    return (
        <Box className={classes.root}>
            <Paper className={classes.paper} elevation={3}>
                <Typography variant="h5" className={classes.title}>
                    Stripe Payment
                </Typography>

                {status === 'loading' && (
                    <>
                        <Typography variant="body1">Verifying your payment...</Typography>
                        <Box mt={2}><CircularProgress className={classes.progress}/></Box>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <Typography variant="h6" className={classes.successText}>
                            Payment successful! 🎉
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            You’ll be redirected shortly...
                        </Typography>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <Typography variant="h6" className={classes.errorText}>
                            {message}
                        </Typography>
                        {
                            redirect &&
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => history.push(redirect)}
                                style={{ marginTop: 16 }}
                            >
                                Try Again
                            </Button>
                        }
                        {
                            message === uniqueCodeErrorMessage &&
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => window.location.reload()}
                                style={{ marginTop: 16 }}
                            >
                                Reload
                            </Button>
                        }
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default StripeSuccessPage;
